import { useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import handleGraphqlError from "./handleGraphqlError";
import { SEND_APP_USAGE_STATS } from "./mutations/stats";
import { useSessionTrackingUsagestats } from "../contexts/sessiontrackingusagestats";
import { detectFormFactor, detectOperatingSystem, APP_USAGE_EVENT } from "../utils/stats";

const selectSelectedRoomsId = (s) => s?.session?.selectedRoomId || {};
const selectAppUrlFromState = (s) => s?.appRef || undefined;

export const useSendAppUsageStats = (callbacks) => {
    const dispatch = useDispatch();

    const { sessionId, projectRef } = useSessionTrackingUsagestats();

    const appUrlFromState = useSelector(selectAppUrlFromState);
    const selectedRoomsId = useSelector(selectSelectedRoomsId);
    const roomId = projectRef ? selectedRoomsId?.[projectRef] ?? null : null;

    const operatingSystem = detectOperatingSystem();

    const [mutate, { loading, error, data }] = useMutation(SEND_APP_USAGE_STATS, {
        onCompleted: (resp) => callbacks?.onCompleted?.(resp),
        onError: (err) => {
            handleGraphqlError(err, dispatch);
            callbacks?.onError?.(err);
        },
    });

    const sendAppUsageStats = (vars, apolloOptions = {}) => {
        const dateTime = Date.now();
        const formFactor = detectFormFactor();

        const shouldAttachRoomId = vars?.event === APP_USAGE_EVENT.ADD_ROOM;

        const defaultData = {
            ...(operatingSystem ? { operatingSystem } : {}),
            ...(formFactor ? { formFactor } : {}),
            ...(shouldAttachRoomId && roomId ? { roomId } : {}),
        };

        const mergedData =
            vars && typeof vars.data === "object"
                ? {
                      ...defaultData,
                      ...vars.data,
                  }
                : Object.keys(defaultData).length
                ? defaultData
                : undefined;

        const eventPayload = {
            dateTime,
            type: vars.type,
            event: vars.event,
            appUrl: appUrlFromState,
            ...(mergedData ? { data: mergedData } : {}),
        };

        return mutate({
            variables: {
                sessionId,
                projectRef,
                event: eventPayload,
            },
            ...apolloOptions,
        });
    };

    return { sendAppUsageStats, loading, error, data };
};
